import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Phone, CheckCircle2, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Public self check-in for tethered headshot shoots (Turbo Headshots v2).
// Subjects scan a QR at the studio, type their details, and land in the
// photographer's waiting queue.
const CheckinPage = () => {
  const { slug } = useParams();
  const [phase, setPhase] = useState('loading'); // loading|notFound|closed|form|submitting|success
  const [shoot, setShoot] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [trap, setTrap] = useState(''); // honeypot — non-profile name so autofill never touches it
  const [error, setError] = useState('');
  const [position, setPosition] = useState(null);
  const [already, setAlready] = useState(false);

  const storageKey = `checkin_${slug}`;

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/checkin/${slug}`);
        if (!live) return;
        setShoot(res.data);
        if (!res.data.open) { setPhase('closed'); return; }
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            // The queue entry expires server-side after 12h — a stale success
            // screen on a re-scan (or a shared kiosk device) must not stick.
            const fresh = parsed.at && Date.now() - parsed.at < 12 * 3600 * 1000;
            if (fresh) {
              setName(parsed.name || '');
              setPosition(parsed.position ?? null);
              setPhase('success');
              return;
            }
            localStorage.removeItem(storageKey);
          } catch { /* fall through to form */ }
        }
        setPhase('form');
      } catch (err) {
        if (!live) return;
        setPhase(err?.response?.status === 404 ? 'notFound' : 'error');
      }
    })();
    return () => { live = false; };
  }, [slug, storageKey]);

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (!email.trim() || !validEmail(email)) { setError('Please enter a valid email address'); return; }
    setPhase('submitting');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/checkin/${slug}`, {
        name: name.trim(), email: email.trim(), mobile: mobile.trim(), contact_ref_x: trap,
      });
      setPosition(res.data.position ?? null);
      setAlready(false);
      localStorage.setItem(storageKey, JSON.stringify({
        entryId: res.data.entry_id, name: name.trim(), position: res.data.position ?? null, at: Date.now(),
      }));
      setPhase('success');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) { setPhase('closed'); return; }
      setError(status === 429
        ? 'Please wait a moment and try again'
        : (err?.response?.data?.detail || 'Something went wrong — please try again'));
      setPhase('form');
    }
  };

  const card = (children) => (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        {children}
      </div>
    </div>
  );

  if (phase === 'loading') {
    return card(<p className="text-center text-neutral-500">Loading…</p>);
  }

  if (phase === 'notFound') {
    return card(
      <div className="text-center space-y-3">
        <Camera className="w-10 h-10 mx-auto text-neutral-400" />
        <h1 className="text-xl font-semibold">This check-in link isn't valid</h1>
        <p className="text-neutral-500 text-sm">Please check with the photographer for the correct QR code.</p>
      </div>
    );
  }

  if (phase === 'error') {
    return card(
      <div className="text-center space-y-4">
        <Camera className="w-10 h-10 mx-auto text-neutral-400" />
        <h1 className="text-xl font-semibold">Couldn't load check-in</h1>
        <p className="text-neutral-500 text-sm">Check your connection, then try again.</p>
        <Button onClick={() => window.location.reload()} className="h-11 bg-neutral-900 hover:bg-neutral-800">Try again</Button>
      </div>
    );
  }

  if (phase === 'closed') {
    return card(
      <div className="text-center space-y-3">
        <Camera className="w-10 h-10 mx-auto text-neutral-400" />
        <h1 className="text-xl font-semibold">Check-in has closed</h1>
        <p className="text-neutral-500 text-sm">
          {shoot?.shoot_name ? `Check-in for ${shoot.shoot_name} is no longer open.` : 'This shoot is no longer accepting check-ins.'}
        </p>
      </div>
    );
  }

  if (phase === 'success') {
    const firstName = name.trim().split(/\s+/)[0] || 'there';
    return card(
      <div className="text-center space-y-4">
        <CheckCircle2 className="w-14 h-14 mx-auto text-green-600" />
        <h1 className="text-2xl font-semibold">
          {already ? "You're already checked in" : `You're checked in, ${firstName}!`}
        </h1>
        <p className="text-neutral-600">Step up when your name is called.</p>
        {position != null && position > 1 && (
          <p className="text-sm text-neutral-500">You're number {position} in line.</p>
        )}
        <button
          type="button"
          className="text-sm text-neutral-400 underline underline-offset-2"
          onClick={() => { localStorage.removeItem(storageKey); setName(''); setEmail(''); setMobile(''); setPhase('form'); }}
        >
          Not you? Check in as someone else
        </button>
      </div>
    );
  }

  return card(
    <form onSubmit={submit} className="space-y-5" autoComplete="on">
      <div className="text-center space-y-1 mb-2">
        <p className="text-xs uppercase tracking-widest text-neutral-400 font-mono">Self check-in</p>
        <h1 className="text-2xl font-semibold leading-tight">
          Welcome to {shoot?.shoot_name || 'the shoot'}
        </h1>
        <p className="text-sm text-neutral-500">Type your details, then step up for your headshot.</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ci-name" className="flex items-center gap-2"><User className="w-4 h-4" /> Full name</Label>
        <Input id="ci-name" value={name} onChange={(e) => setName(e.target.value)}
          autoComplete="name" placeholder="Jane Smith" className="h-12 text-base" autoFocus />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ci-email" className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
        <Input id="ci-email" type="email" inputMode="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email" placeholder="jane@company.com" className="h-12 text-base" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ci-mobile" className="flex items-center gap-2"><Phone className="w-4 h-4" /> Mobile <span className="text-neutral-400 font-normal">(for photo delivery)</span></Label>
        <Input id="ci-mobile" type="tel" inputMode="tel" value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          autoComplete="tel" placeholder="0412 345 678" className="h-12 text-base" />
      </div>

      {/* Honeypot — visually hidden; non-profile field name + one-time-code
          autocomplete so browser autofill never populates it */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }} aria-hidden="true">
        <input tabIndex={-1} autoComplete="one-time-code" name="contact_ref_x" value={trap}
          onChange={(e) => setTrap(e.target.value)} />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <Button type="submit" disabled={phase === 'submitting'}
        className="w-full h-12 text-base bg-neutral-900 hover:bg-neutral-800">
        {phase === 'submitting' ? 'Checking you in…' : 'Check in'}
      </Button>
    </form>
  );
};

export default CheckinPage;
